import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, HStack } from "@astryxdesign/core/Stack";
import { Text, Heading } from "@astryxdesign/core/Text";
import { Card } from "@astryxdesign/core/Card";
import { Badge } from "@astryxdesign/core/Badge";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Selector } from "@astryxdesign/core/Selector";
import { TextInput } from "@astryxdesign/core/TextInput";
import { CheckboxInput } from "@astryxdesign/core/CheckboxInput";
import { Tooltip } from "@astryxdesign/core/Tooltip";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Table, proportional, pixel } from "@astryxdesign/core/Table";
import { useAuth } from "../../contexts/AuthContext";
import { usersAPI, handleAPIError } from "../../services/api";
import AstryxTheme from "../../theme/AstryxTheme";

const ROLE_OPTIONS = [
  { value: "normal", label: "Normal" },
  { value: "admin", label: "Administrador" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  role: "normal",
  password: "",
  access_hub_nfse: true,
  access_monitor_360: false,
};

export default function UserManagement() {
  const { hasPermission, user: currentUser } = useAuth();

  // State management
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await usersAPI.getAll();

      if (response.success) {
        // Filter to show only admin and normal users (not clients)
        const officeUsers = response.data.users.filter(
          (user) => user.role === "admin" || user.role === "normal"
        );
        setUsers(officeUsers);
      } else {
        setError(response.message);
      }
    } catch (error) {
      const errorMessage = handleAPIError(error);
      console.error("Error loading users:", error);
      setError(errorMessage || "Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
  const openAddUserModal = () => {
    setFormData({ ...EMPTY_FORM });
    setIsAddModalOpen(true);
  };

  const closeAddUserModal = () => {
    setIsAddModalOpen(false);
    setFormData({ ...EMPTY_FORM });
  };

  const openEditUserModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      password: "", // Don't prefill password
      access_hub_nfse: user.access_hub_nfse !== false,
      access_monitor_360: user.access_monitor_360 === true,
    });
    setIsEditModalOpen(true);
  };

  const closeEditUserModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    setFormData({ ...EMPTY_FORM });
  };

  // Campos controlados (inputs do Astryx entregam o valor direto)
  const setField = (name) => (value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Substitui o `required` nativo dos inputs antigos (o TextInput do Astryx
    // marca aria-required mas não bloqueia o submit sozinho).
    if (!formData.name.trim() || !formData.email.trim()) {
      alert("Nome e e-mail são obrigatórios.");
      return;
    }
    if (isAddModalOpen && !formData.password) {
      alert("Informe uma senha para o novo usuário.");
      return;
    }

    try {
      let result;

      if (isAddModalOpen) {
        // Create new user
        const { password, access_hub_nfse, access_monitor_360, ...rest } =
          formData;
        result = await usersAPI.create({
          ...rest,
          password,
          access_hub_nfse,
          access_monitor_360,
        });
        if (result.success) {
          await loadUsers(); // Refresh list
          closeAddUserModal();
          alert("Usuário criado com sucesso!");
        }
      } else if (isEditModalOpen && editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          access_hub_nfse: formData.access_hub_nfse,
          access_monitor_360: formData.access_monitor_360,
        };

        // Only include password if provided
        if (formData.password) {
          updateData.password = formData.password;
        }

        result = await usersAPI.update(editingUser.id, updateData);
        if (result.success) {
          await loadUsers(); // Refresh list
          closeEditUserModal();
          alert("Usuário atualizado com sucesso!");
        }
      }

      if (!result.success) {
        alert(`Erro: ${result.message}`);
      }
    } catch (error) {
      const errorMessage = handleAPIError(error) || "Erro ao salvar usuário";
      console.error("Save user error:", error);
      alert(errorMessage);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Tem certeza que deseja deletar este usuário?")) {
      try {
        const result = await usersAPI.delete(userId);
        if (result.success) {
          await loadUsers(); // Refresh list
          alert("Usuário deletado com sucesso!");
        } else {
          alert(`Erro: ${result.message}`);
        }
      } catch (error) {
        const errorMessage = handleAPIError(error) || "Erro ao deletar usuário";
        console.error("Delete user error:", error);
        alert(errorMessage);
      }
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const result = await usersAPI.toggleStatus(userId);
      if (result.success) {
        await loadUsers();
        const newStatus = result.data?.status;
        const statusText = newStatus === "active" ? "ativado" : "desativado";
        alert(`Usuário ${statusText} com sucesso!`);
      } else {
        const targetStatus = currentStatus === "active" ? "inactive" : "active";
        const updateRes = await usersAPI.update(userId, {
          status: targetStatus,
        });
        if (updateRes.success) {
          await loadUsers();
          const statusText =
            targetStatus === "active" ? "ativado" : "desativado";
          alert(`Usuário ${statusText} com sucesso!`);
        } else {
          alert(`Erro: ${result.message || updateRes.message}`);
        }
      }
    } catch (error) {
      try {
        const targetStatus = currentStatus === "active" ? "inactive" : "active";
        const updateRes = await usersAPI.update(userId, {
          status: targetStatus,
        });
        if (updateRes.success) {
          await loadUsers();
          const statusText =
            targetStatus === "active" ? "ativado" : "desativado";
          alert(`Usuário ${statusText} com sucesso!`);
          return;
        }
      } catch (_) {}
      const errorMessage =
        handleAPIError(error) || "Erro ao alterar status do usuário";
      console.error("Toggle user status error", error);
      alert(errorMessage);
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      const result = await usersAPI.unlock(userId);
      if (result.success) {
        await loadUsers();
        alert("Usuário desbloqueado com sucesso!");
      } else {
        alert(`Erro: ${result.message}`);
      }
    } catch (error) {
      const errorMessage =
        handleAPIError(error) || "Erro ao desbloquear usuário";
      console.error("Unlock user error:", error);
      alert(errorMessage);
    }
  };

  // Conta travada pelo lockout de login (locked_until no futuro)
  const isUserLocked = (user) =>
    !!user.locked_until && new Date(user.locked_until) > new Date();

  const formatDate = (dateString) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const productYesNo = (flag) =>
    flag ? (
      <Text color="accent" weight="medium">
        Sim
      </Text>
    ) : (
      <Text type="supporting">Não</Text>
    );

  // Colunas da tabela (data-driven)
  const tableColumns = [
    {
      key: "id",
      header: "ID",
      width: pixel(60),
      renderCell: (u) => <Text size="sm">{u.id}</Text>,
    },
    {
      key: "name",
      header: "Nome",
      width: proportional(2),
      renderCell: (u) => (
        <Text weight="semibold" size="sm">
          {u.name}
        </Text>
      ),
    },
    {
      key: "email",
      header: "Email",
      width: proportional(3),
      renderCell: (u) => <Text size="sm">{u.email}</Text>,
    },
    {
      key: "role",
      header: "Função",
      width: pixel(140),
      renderCell: (u) => (
        <Badge
          variant={u.role === "admin" ? "error" : "info"}
          label={u.role === "admin" ? "Administrador" : "Normal"}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      width: pixel(150),
      renderCell: (u) => (
        <HStack gap={1} vAlign="center">
          <Badge
            variant={u.status === "active" ? "success" : "neutral"}
            label={u.status === "active" ? "Ativo" : "Inativo"}
          />
          {isUserLocked(u) && (
            <Tooltip
              content={`Bloqueado até ${new Date(
                u.locked_until
              ).toLocaleString("pt-BR")}`}
            >
              <Badge variant="warning" label="Bloqueado" />
            </Tooltip>
          )}
        </HStack>
      ),
    },
    {
      key: "hub",
      header: "Hub NFSe",
      width: pixel(100),
      renderCell: (u) => productYesNo(u.access_hub_nfse !== false),
    },
    {
      key: "monitor",
      header: "Monitor 360",
      width: pixel(110),
      renderCell: (u) => productYesNo(u.access_monitor_360 === true),
    },
    {
      key: "last_login",
      header: "Último Login",
      width: pixel(120),
      renderCell: (u) => (
        <Text type="supporting">{formatDate(u.last_login)}</Text>
      ),
    },
    {
      key: "created_at",
      header: "Criação",
      width: pixel(120),
      renderCell: (u) => (
        <Text type="supporting">{formatDate(u.created_at)}</Text>
      ),
    },
    {
      key: "actions",
      header: "Ações",
      width: pixel(160),
      renderCell: (u) => {
        const isSelf = currentUser && currentUser.id === u.id;
        const active = u.status === "active";
        return (
          <HStack gap={1}>
            <IconButton
              label="Editar usuário"
              tooltip="Editar usuário"
              icon={<FontAwesomeIcon icon="edit" />}
              variant="ghost"
              size="sm"
              onClick={() => openEditUserModal(u)}
            />
            {isUserLocked(u) && (
              <IconButton
                label="Desbloquear usuário"
                tooltip="Desbloquear usuário"
                icon={<FontAwesomeIcon icon="lock-open" />}
                variant="ghost"
                size="sm"
                onClick={() => handleUnlockUser(u.id)}
              />
            )}
            <IconButton
              label={active ? "Desativar usuário" : "Ativar usuário"}
              tooltip={
                isSelf
                  ? "Você não pode alterar seu próprio status"
                  : active
                    ? "Desativar usuário"
                    : "Ativar usuário"
              }
              icon={<FontAwesomeIcon icon={active ? "pause" : "play"} />}
              variant="ghost"
              size="sm"
              isDisabled={isSelf}
              onClick={() => handleToggleStatus(u.id, u.status)}
            />
            <IconButton
              label="Deletar usuário"
              tooltip="Deletar usuário"
              icon={<FontAwesomeIcon icon="trash" />}
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteUser(u.id)}
            />
          </HStack>
        );
      },
    },
  ];

  // Check permissions
  if (!hasPermission("admin")) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <EmptyState
            title="Acesso Negado"
            description="Você não tem permissão para acessar esta área."
            icon={<FontAwesomeIcon icon="lock" />}
          />
        </Card>
      </AstryxTheme>
    );
  }

  // Loading state
  if (loading) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <Stack gap={2}>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={48} index={i} />
            ))}
            <Text type="supporting" justify="center">
              Carregando usuários...
            </Text>
          </Stack>
        </Card>
      </AstryxTheme>
    );
  }

  // Error state
  if (error) {
    return (
      <AstryxTheme>
        <Card padding={6}>
          <Stack gap={3} hAlign="center">
            <EmptyState
              title="Erro ao Carregar Usuários"
              description={error}
              icon={<FontAwesomeIcon icon="exclamation-triangle" />}
            />
            <Button
              label="Tentar Novamente"
              variant="primary"
              icon={<FontAwesomeIcon icon="sync" />}
              onClick={loadUsers}
            />
          </Stack>
        </Card>
      </AstryxTheme>
    );
  }

  return (
    <AstryxTheme>
      <Stack as="section" aria-label="Gerenciamento de Usuários" gap={4} width="100%">
        {/* Cabeçalho: título + ações */}
        <HStack gap={3} vAlign="center" hAlign="between" wrap="wrap">
          <HStack gap={2} vAlign="center">
            <Heading level={2}>Gerenciamento de Usuários do Escritório</Heading>
            <Text type="supporting">
              ({users.length} usuário{users.length !== 1 ? "s" : ""})
            </Text>
          </HStack>
          <HStack gap={2} vAlign="center">
            <IconButton
              label="Atualizar lista"
              tooltip="Atualizar lista"
              icon={<FontAwesomeIcon icon="sync" />}
              variant="ghost"
              onClick={loadUsers}
            />
            <Button
              label="Adicionar Usuário"
              variant="primary"
              icon={<FontAwesomeIcon icon="plus" />}
              onClick={openAddUserModal}
            />
          </HStack>
        </HStack>

        {/* Tabela / estado vazio */}
        {users.length === 0 ? (
          <Card padding={6}>
            <EmptyState
              title="Nenhum usuário do escritório encontrado"
              description="Adicione usuários administradores e normais para gerenciar o sistema."
              icon={<FontAwesomeIcon icon="users-cog" />}
            />
          </Card>
        ) : (
          <Table
            data={users}
            columns={tableColumns}
            idKey="id"
            hasHover
            density="balanced"
          />
        )}

        {/* Modal de Adicionar Usuário */}
        {isAddModalOpen && (
          <Dialog
            isOpen
            onOpenChange={(open) => {
              if (!open) closeAddUserModal();
            }}
            width={520}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap={3}>
                <DialogHeader
                  title="Adicionar Novo Usuário"
                  onOpenChange={(open) => {
                    if (!open) closeAddUserModal();
                  }}
                  hasDivider
                />
                <TextInput
                  label="Nome Completo"
                  isRequired
                  hasAutoFocus
                  value={formData.name}
                  onChange={setField("name")}
                />
                <TextInput
                  label="Email"
                  isRequired
                  type="email"
                  htmlName="email"
                  value={formData.email}
                  onChange={setField("email")}
                />
                <Selector
                  label="Função"
                  isRequired
                  options={ROLE_OPTIONS}
                  value={formData.role}
                  onChange={setField("role")}
                />
                <Stack gap={2}>
                  <CheckboxInput
                    label="Acesso ao Hub NFSe"
                    value={formData.access_hub_nfse}
                    onChange={setField("access_hub_nfse")}
                  />
                  <CheckboxInput
                    label="Acesso ao Monitor 360"
                    value={formData.access_monitor_360}
                    onChange={setField("access_monitor_360")}
                  />
                </Stack>
                <TextInput
                  label="Senha"
                  isRequired
                  type="password"
                  htmlName="password"
                  value={formData.password}
                  onChange={setField("password")}
                />
                <HStack gap={2} hAlign="end">
                  <Button
                    label="Cancelar"
                    variant="ghost"
                    onClick={closeAddUserModal}
                  />
                  <Button
                    label="Adicionar Usuário"
                    variant="primary"
                    type="submit"
                    icon={<FontAwesomeIcon icon="plus" />}
                  />
                </HStack>
              </Stack>
            </form>
          </Dialog>
        )}

        {/* Modal de Editar Usuário */}
        {isEditModalOpen && editingUser && (
          <Dialog
            isOpen
            onOpenChange={(open) => {
              if (!open) closeEditUserModal();
            }}
            width={520}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap={3}>
                <DialogHeader
                  title="Editar Usuário"
                  onOpenChange={(open) => {
                    if (!open) closeEditUserModal();
                  }}
                  hasDivider
                />
                <TextInput
                  label="Nome Completo"
                  isRequired
                  hasAutoFocus
                  value={formData.name}
                  onChange={setField("name")}
                />
                <TextInput
                  label="Email"
                  isRequired
                  type="email"
                  htmlName="email"
                  value={formData.email}
                  onChange={setField("email")}
                />
                <Selector
                  label="Função"
                  isRequired
                  options={ROLE_OPTIONS}
                  value={formData.role}
                  onChange={setField("role")}
                />
                <Stack gap={2}>
                  <CheckboxInput
                    label="Acesso ao Hub NFSe"
                    value={formData.access_hub_nfse}
                    onChange={setField("access_hub_nfse")}
                  />
                  <CheckboxInput
                    label="Acesso ao Monitor 360"
                    value={formData.access_monitor_360}
                    onChange={setField("access_monitor_360")}
                  />
                </Stack>
                <TextInput
                  label="Nova Senha"
                  isOptional
                  type="password"
                  htmlName="password"
                  description="Deixe em branco para manter a senha atual."
                  placeholder="Deixe em branco para manter a senha atual"
                  value={formData.password}
                  onChange={setField("password")}
                />
                <HStack gap={2} hAlign="end">
                  <Button
                    label="Cancelar"
                    variant="ghost"
                    onClick={closeEditUserModal}
                  />
                  <Button
                    label="Salvar Alterações"
                    variant="primary"
                    type="submit"
                    icon={<FontAwesomeIcon icon="save" />}
                  />
                </HStack>
              </Stack>
            </form>
          </Dialog>
        )}
      </Stack>
    </AstryxTheme>
  );
}
